// Lightweight localStorage-backed game state store with subscription.
import { useEffect, useState, useSyncExternalStore } from "react";

const STORAGE_KEY = "crossbow_chaos_state_v1";

const DEFAULT_STATE = {
  coins: 150,
  level: 1,
  bestScore: 0,
  owned: {
    crossbow: ["crossbow_wooden"],
    arrow: ["arrow_normal"],
    skin: ["skin_default"],
  },
  equipped: {
    crossbow: "crossbow_wooden",
    arrow: "arrow_normal",
    skin: "skin_default",
  },
  settings: { sound: true },
  miniGamesWon: {},
  streak: { count: 0, lastDate: null },
  gamesPlayed: 0,
  reviewPrompted: false,
};

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    // shallow merge with defaults to add new fields on upgrades
    return {
      ...DEFAULT_STATE,
      ...parsed,
      owned: { ...DEFAULT_STATE.owned, ...(parsed.owned || {}) },
      equipped: { ...DEFAULT_STATE.equipped, ...(parsed.equipped || {}) },
      settings: { ...DEFAULT_STATE.settings, ...(parsed.settings || {}) },
      miniGamesWon: { ...(parsed.miniGamesWon || {}) },
      streak: { ...DEFAULT_STATE.streak, ...(parsed.streak || {}) },
    };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

let state = load();
const listeners = new Set();

function save() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

function set(next) {
  state = typeof next === "function" ? next(state) : { ...state, ...next };
  save();
  listeners.forEach((l) => l());
}

export const gameStore = {
  get: () => state,
  subscribe: (l) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  addCoins: (amount) => set((s) => ({ ...s, coins: Math.max(0, s.coins + amount) })),
  spendCoins: (amount) => {
    if (state.coins < amount) return false;
    set((s) => ({ ...s, coins: s.coins - amount }));
    return true;
  },
  buy: (category, itemId, price) => {
    if (state.owned[category]?.includes(itemId)) return false;
    if (state.coins < price) return false;
    set((s) => ({
      ...s,
      coins: s.coins - price,
      owned: { ...s.owned, [category]: [...s.owned[category], itemId] },
      equipped: { ...s.equipped, [category]: itemId },
    }));
    return true;
  },
  equip: (category, itemId) => {
    if (!state.owned[category]?.includes(itemId)) return false;
    set((s) => ({ ...s, equipped: { ...s.equipped, [category]: itemId } }));
    return true;
  },
  setLevel: (level) => set((s) => ({ ...s, level: Math.max(s.level, level) })),
  setBestScore: (score) =>
    set((s) => ({ ...s, bestScore: Math.max(s.bestScore, score) })),
  markMiniGameWon: (id) =>
    set((s) => {
      const next = {
        ...s,
        miniGamesWon: { ...s.miniGamesWon, [id]: (s.miniGamesWon[id] || 0) + 1 },
      };
      const grant = (category, itemId) => {
        next.owned = next.owned || s.owned;
        if (!next.owned[category].includes(itemId)) {
          next.owned = {
            ...next.owned,
            [category]: [...next.owned[category], itemId],
          };
        }
      };
      if (id === "trophy_trial") {
        grant("crossbow", "crossbow_champion");
        grant("skin", "skin_champion");
      }
      if (id === "boss") {
        grant("crossbow", "crossbow_boss_slayer");
        grant("skin", "skin_boss_slayer");
      }
      if (id === "chain_master") {
        grant("crossbow", "crossbow_chain");
        grant("skin", "skin_chain");
        grant("arrow", "arrow_chain");
      }
      if (id === "skull_dodge") {
        grant("crossbow", "crossbow_skeleton");
        grant("skin", "skin_skeleton");
        grant("arrow", "arrow_skeleton");
      }
      return next;
    }),
  claimItem: (category, itemId) =>
    set((s) => ({
      ...s,
      owned: { ...s.owned, [category]: [...s.owned[category], itemId] },
    })),
  incrementGamesPlayed: () => {
    set((s) => ({ ...s, gamesPlayed: (s.gamesPlayed || 0) + 1 }));
    return state.gamesPlayed;
  },
  markReviewPrompted: () => set((s) => ({ ...s, reviewPrompted: true })),
  toggleSound: () =>
    set((s) => ({ ...s, settings: { ...s.settings, sound: !s.settings.sound } })),
  reset: () => {
    state = { ...DEFAULT_STATE };
    save();
    listeners.forEach((l) => l());
  },
  // Daily streak: call once on app load. Returns the bonus amount if a new
  // day was registered (0 if already claimed today).
  checkDailyBonus: () => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const last = state.streak?.lastDate;
    if (last === today) return { bonus: 0, streak: state.streak.count };
    // streak logic: if yesterday -> +1, else reset to 1
    const y = new Date();
    y.setDate(y.getDate() - 1);
    const yesterday = y.toISOString().slice(0, 10);
    const nextCount = last === yesterday ? (state.streak.count || 0) + 1 : 1;
    const bonus = Math.min(100 + (nextCount - 1) * 50, 500);
    set((s) => ({
      ...s,
      coins: s.coins + bonus,
      streak: { count: nextCount, lastDate: today },
    }));
    return { bonus, streak: nextCount };
  },
};

export function useGameStore() {
  return useSyncExternalStore(gameStore.subscribe, gameStore.get, gameStore.get);
}

// Optional: hook for just a slice
export function useCoins() {
  const s = useGameStore();
  return s.coins;
}
